/**
 * CV builder autocomplete — companies, job titles and universities.
 *
 * These corpora used to ship to the browser as JSON and be filtered there; the company
 * list alone was 1.1 MB. Matching happens on the server now, so a keystroke costs a few
 * matches instead of the whole corpus.
 *
 * Each function returns the *local* shape the CV suggest components already render, so
 * the API path and the bundled fallback are interchangeable. They return `null` — not an
 * empty array — when the request fails, which is how the caller tells "no matches" from
 * "server unreachable" and decides whether to fall back.
 */

import http from '../client';
import { ENDPOINTS } from '../config';

const search = async (endpoint, q, limit) => {
  try {
    const res = await http.get(endpoint, { q, limit }, { skipAuth: true });
    return res.success && Array.isArray(res.data) ? res.data : null;
  } catch {
    return null;
  }
};

/** @returns {Promise<Array<{n: string, i?: string, logo?: string}>|null>} */
export const searchCompanies = async (q, limit = 8) => {
  const rows = await search(ENDPOINTS.CV_REFERENCE.COMPANIES, q, limit);
  // `logo` rather than `logoUrl`: it is the prop the suggest list already passes to
  // OrgLogo, which until now was always undefined because nothing supplied it.
  return rows && rows.map((r) => ({ n: r.name, i: r.industryName, logo: r.logoUrl }));
};

/** @returns {Promise<Array<{n: string, e?: string, i?: string}>|null>} */
export const searchPositions = async (q, limit = 8) => {
  const rows = await search(ENDPOINTS.CV_REFERENCE.POSITIONS, q, limit);
  return rows && rows.map((r) => ({ n: r.name, e: r.enName, i: r.industryName }));
};

/** @returns {Promise<Array<{name: string, abbr: string, main: boolean, logo?: string}>|null>} */
export const searchUniversities = async (q, limit = 8) => {
  const rows = await search(ENDPOINTS.CV_REFERENCE.UNIVERSITIES, q, limit);
  return rows && rows.map((r) => ({ name: r.name, abbr: r.abbr, main: !!r.isPrimary, logo: r.logoUrl }));
};

/**
 * Logos for names already saved on a CV, in one request.
 *
 * Keys are normalized by the caller (src/data/logoDirectory.js) and joined with "|" — a
 * character normalization cannot produce, so it can never appear inside a key.
 *
 * @param {string[]} keys normalized name keys
 * @returns {Promise<Array<{key: string, logoUrl: string}>|null>} null when the request fails
 */
export const searchLogos = async (keys) => {
  if (!keys || keys.length === 0) return [];
  try {
    const res = await http.get(ENDPOINTS.CV_REFERENCE.LOGOS, { keys: keys.join("|") }, { skipAuth: true });
    return res.success && Array.isArray(res.data) ? res.data : null;
  } catch {
    return null;
  }
};

/**
 * Logos of companies registered on this platform, name and URL as stored.
 *
 * The caller keys these itself — see src/data/logoDirectory.js. ~190 rows, fetched once
 * per session.
 *
 * @returns {Promise<Array<{name: string, logoUrl: string}>|null>} null when the request fails
 */
export const registeredLogos = async () => {
  try {
    const res = await http.get(ENDPOINTS.CV_REFERENCE.REGISTERED_LOGOS, undefined, { skipAuth: true });
    if (res.success && Array.isArray(res.data)) return res.data;
  } catch {
    // falls through to the list below
  }
  // /cv-reference/registered-logos is not on the deployed hrSystemServer (404), which is
  // why a registered employer typed on a CV — Метаког Метрикс, say — showed the generic
  // icon while its uploaded logo was on its own company row all along. The companies
  // list carries the same name + photo pair and is public, so it answers until the
  // dedicated route ships.
  try {
    const res = await http.get(ENDPOINTS.ASSESSMENT_COMPANIES.LIST_WITH_BENEFITS, undefined, { skipAuth: true });
    return res.success && Array.isArray(res.data)
      ? res.data.filter((r) => r?.name && r?.photoUrl).map((r) => ({ name: r.name, logoUrl: r.photoUrl }))
      : null;
  } catch {
    return null;
  }
};

const cvReferenceService = {
  searchCompanies,
  searchPositions,
  searchUniversities,
  searchLogos,
  registeredLogos,
};

export default cvReferenceService;
